import React, { useEffect, useRef, useState, useMemo } from "react";
import { YStack, XStack, Text, Button, ScrollView, View, Spinner } from "tamagui";
import { useNavigate } from "react-router-dom";
import { colors } from "@styles/colors";
import { RiTimeLine } from "react-icons/ri";
import { isGasSkinV2, SkinTabs, v2 } from "@screen/Home/pages/GasShop/skin";
import { ORDER_STS, OrderStatusBadge } from "./components/OrderStatusBadge";
import { OrderCardV2 } from "./components/OrderCardV2";
import { listOrders } from "./API/handleOrderAPI";
import { isLiveOrderStatus } from "./orderRefresh";
import { useOrderRefresh } from "./useOrderRefresh";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { OrderListItem, OrderListStatusEnum } from "./type/orderList";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { createCoalescedRefresher } from "@utils/realtime/coalescedRefresh";
import { REALTIME_REFRESH_DEBOUNCE_MS, REALTIME_REFRESH_MAX_WAIT_MS } from "@utils/realtime/orderRealtimeSession";
import { useOrderRealtimeListener } from "@utils/realtime/useOrderRealtimeListener";

type Status = "pending" | "waiting_for_pickup" | "delivering" | "completed" | "canceled";

/** 狀態色票與小標已抽到 components/OrderStatusBadge（訂單列表／帳戶訂單紀錄共用同一份） */
const STS = ORDER_STS;

const mapAPIStatusToUI = (status: OrderListStatusEnum): Status => {
  switch (status) {
    case OrderListStatusEnum.pending:
      return "pending";
    case OrderListStatusEnum.waiting_for_pickup:
      return "waiting_for_pickup";
    case OrderListStatusEnum.delivering:
      return "delivering";
    case OrderListStatusEnum.completed:
      return "completed";
    case OrderListStatusEnum.canceled:
      return "canceled";
    default:
      return "pending";
  }
};

interface OrderCardProps {
  o: OrderListItem;
  onPress: () => void;
}

const OrderCard = ({ o, onPress }: OrderCardProps) => {
  const skinV2 = isGasSkinV2();
  const uiStatus = mapAPIStatusToUI(o?.status);
  const s = STS[uiStatus];
  // 該瓦斯行開啟隱藏金額設定、且訂單為待確認 → 不顯示金額（待瓦斯行確認後才顯示）
  const hideAmount = isOrderAmountHidden(o.hide_amount_on_pending, uiStatus);

  const statusBadge = <OrderStatusBadge status={uiStatus} refundState={o.refund_state} />;

  const time = o.created_at ? format(new Date(o.created_at), "MM/dd HH:mm") : "-";

  const body = (
    <>
      <XStack jc="space-between" ai="flex-start">
        <YStack f={1} pr="$2">
          <Text fontSize={14} fow="700" numberOfLines={1}>
            {o.owner?.label || "配送地址"}
          </Text>
          <Text fontSize={12} color={colors.gray}>
            #{o.display_id}
          </Text>
        </YStack>
        <XStack ai="center" gap={5} fw="wrap" jc="flex-end">
          {statusBadge}
        </XStack>
      </XStack>
      <View bg={colors.bg} br={6} px="$2.5" py="$1.5" width="100%">
        <Text fontSize={13} color={colors.text} numberOfLines={1} ellipsizeMode="tail">
          {o.items_summary.map((it) => `${it.pn} x${it.qty}`).join(", ")}
        </Text>
      </View>
      <XStack jc="space-between" ai="center">
        <XStack ai="center" gap={4}>
          <RiTimeLine size={13} color={colors.gray200} />
          <Text fontSize={12} color={colors.gray200}>
            {time}
          </Text>
        </XStack>
        {!hideAmount && (
          <Text fontSize={18} fow="800" color={colors.primary} ls={-0.5}>
            NT$ {o.total.toLocaleString()}
          </Text>
        )}
      </XStack>
    </>
  );

  // V2 卡片與帳戶的訂單紀錄共用同一張（見 components/OrderCardV2）
  if (skinV2) return <OrderCardV2 o={o} onPress={onPress} />;

  return (
    <YStack
      bg={colors.light}
      br={10}
      ov="hidden"
      onPress={onPress}
      pressStyle={{ scale: 0.98 }}
      animation="quick"
      shadowColor="rgba(0,0,0,0.05)"
      shadowRadius={5}
      shadowOffset={{ width: 0, height: 2 }}>
      <YStack p="$3" gap="$2">
        {body}
      </YStack>
    </YStack>
  );
};

const OrderList = () => {
  const skinV2 = isGasSkinV2();
  const navigate = useNavigate();
  const { chooseIdentity } = useUser();
  const { cusJwtToken } = useCusAuth();
  const [stsF, setStsF] = useState<string>("all");
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const inFlightRef = useRef(false);
  /** 即時事件要重抓時剛好有查詢在跑（初次載入或輪詢）：先記著，等那次結束再抓 */
  const realtimeDeferredRef = useRef(false);
  const realtimeRefresherRef = useRef<ReturnType<typeof createCoalescedRefresher> | null>(null);

  /** @param opts.silent 背景更新用：不切換 loading、失敗也不跳提示，畫面維持現狀 */
  const fetchOrders = async (opts?: { silent?: boolean }) => {
    if (!cusJwtToken) return;
    const silent = opts?.silent ?? false;

    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const res = await listOrders({
        jwtToken: cusJwtToken,
        // chooseIdentity === null 對應「全部」分頁：不送 X-Context 讓 BE 回混合 feed
        xContext: buildXContextHeader(chooseIdentity),
      });

      if (res.state === "success") {
        setOrders(res.data.data.items || []);
      } else if (!silent) {
        toast.error("取得訂單列表失敗", { id: "fetch-orders-error" });
      }
    } finally {
      inFlightRef.current = false;
      if (!silent) setLoading(false);
      // 查詢途中來了即時事件：這次的結果可能是變化前的，結束後再補抓一次
      if (realtimeDeferredRef.current) {
        realtimeDeferredRef.current = false;
        realtimeRefresherRef.current?.request();
      }
    }
  };
  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    fetchOrders();
  }, [chooseIdentity?.context_id, cusJwtToken]);

  /**
   * 狀態是由瓦斯行後台推進的（接單、出發、完成），而 LIFF 收不到 push。
   * 列表原本只在掛載時查一次，客戶看著它也不會變。全部訂單都進終局狀態就自己停。
   */
  useOrderRefresh({
    active: true,
    ready: !!cusJwtToken,
    hasLive: orders.some((o) => isLiveOrderStatus(mapAPIStatusToUI(o.status as OrderListStatusEnum))),
    externalInFlight: loading,
    run: () => fetchOrders({ silent: true }),
  });

  /**
   * 訂單即時通知（AppSync Events，連線由 PageWrapper 管）：瓦斯行／配送員改單、mirror 同步完，
   * 約 10 秒內就重抓，不必等上面那支 30 秒輪詢。列表上什麼單都可能變，所以任何事件都重抓。
   *
   * 已有查詢在跑時不疊打：兩個回應先後不定，舊的可能蓋掉新的；改成等它結束再補一次。
   */
  useEffect(() => {
    const refresher = createCoalescedRefresher({
      run: () => {
        if (inFlightRef.current) {
          realtimeDeferredRef.current = true;
          return;
        }
        return fetchOrdersRef.current({ silent: true });
      },
      delayMs: REALTIME_REFRESH_DEBOUNCE_MS,
      maxWaitMs: REALTIME_REFRESH_MAX_WAIT_MS,
    });
    realtimeRefresherRef.current = refresher;
    return () => {
      refresher.cancel();
      realtimeRefresherRef.current = null;
    };
  }, []);
  useOrderRealtimeListener({
    onEvent: () => realtimeRefresherRef.current?.request(),
    onResume: () => realtimeRefresherRef.current?.request(),
  });

  const filteredOrders = useMemo(() => {
    if (stsF === "all") return orders;
    return orders.filter((o) => mapAPIStatusToUI(o.status as OrderListStatusEnum) === stsF);
  }, [orders, stsF]);

  if (loading) {
    return (
      <YStack ai="center" jc="center" py="$10">
        {/* V2：載入時整頁只有漸層藍底，深色 spinner／灰字對比不足，改用白色 */}
        <Spinner size="large" color={skinV2 ? "#fff" : colors.primary} />
        <Text mt="$4" color={skinV2 ? "rgba(255,255,255,0.9)" : colors.gray}>
          載入訂單中...
        </Text>
      </YStack>
    );
  }

  /**
   * 狀態篩選。V2 改用底線分頁籤（白字＋白底線），不再是第二排膠囊 chips ——
   * 上方身分切換已經是一排 pill，兩排同形狀同尺寸的 chips 疊在藍色帶上分不出主次，
   * 而且「全部」會出現兩次（一個指身分、一個指狀態），容易誤讀。
   * 換成底線分頁籤後：pill = 你是誰，底線 = 看哪一種狀態，形狀本身就把兩件事分開了。
   */
  const filters: [string, string][] = [
    ["all", "全部"],
    ["pending", "待確認"],
    ["waiting_for_pickup", "待接單"],
    ["delivering", "配送中"],
    ["completed", "已完成"],
    ["canceled", "已取消"],
  ];

  return (
    <YStack>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {skinV2 ? (
          <XStack pr="$8" pb={2}>
            <SkinTabs tone="ink" value={stsF} onChange={setStsF} tabs={filters.map(([key, label]) => ({ key, label }))} />
          </XStack>
        ) : (
          <XStack gap="$2" pr="$8">
            {filters.map(([v, l]) => {
              const isSel = stsF === v;
              const s = v !== "all" ? STS[v as Status] : null;
              return (
                <Button
                  key={v}
                  onPress={() => setStsF(v)}
                  br={20}
                  px="$3.5"
                  h={36}
                  bg={isSel ? (s ? s.bg : colors.primaryActive) : colors.light}
                  borderColor={isSel ? (s ? s.bar : colors.primary) : colors.gray100}
                  borderWidth={1.5}
                  pressStyle={{ opacity: 0.8 }}>
                  <XStack ai="center" gap={5}>
                    {s && isSel && <View w={6} h={6} br={3} bg={s.bar} />}
                    <Text fontSize={12} fow={isSel ? "700" : "400"} color={isSel ? (s ? s.c : colors.primary) : colors.gray}>
                      {l}
                    </Text>
                  </XStack>
                </Button>
              );
            })}
          </XStack>
        )}
      </ScrollView>
      <YStack py={skinV2 ? 14 : "$3"}>
        {filteredOrders.length === 0 ? (
          <YStack ai="center" jc="center" py="$10" gap="$4">
            <Text fontSize={16} fow="600" color={skinV2 ? v2.sub : colors.gray}>
              沒有符合的訂單
            </Text>
          </YStack>
        ) : (
          /* 卡片依序上浮（見 all.scss 的 .gas-stagger-kids）。掛在清單容器上而不是
             PageWrapper 那一層：卡片包在這裡面，從外層數只會數到「整份清單」一個。 */
          <YStack className="gas-stagger-kids" gap={skinV2 ? 12 : "$3"}>
            {filteredOrders.map((o) => (
              <OrderCard
                key={o.platform_order_id}
                o={o}
                onPress={() =>
                  navigate(`/orders/${o.platform_order_id}`, {
                    // supplierName 帶入詳情頁標頭：「全部」分頁 chooseIdentity 為 null，標頭瓦斯行才不會消失
                    state: { displayId: o.display_id, supplierName: o.supplier?.supplier_name },
                  })
                }
              />
            ))}
          </YStack>
        )}
      </YStack>
    </YStack>
  );
};

export default OrderList;
